import { ElementType, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { MEDIA_QUERY } from '@optimus-web/breakpoints';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    FOCUS_OUTLINE_STYLE,
    getSpacingToken,
    getToken,
    InputValue,
} from '@optimus-web/common';
import { Stack } from '../Stack';
import { Typography, TypographyProps } from '../typography';
import { DropdownItemVariant } from './types';

const COLOR_TRANSITION = css`
    transition-property: background-color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.duration10}ms;
`;

interface DropdownItemContainerProps {
    disabled?: boolean;
    selected?: boolean;
    focused?: boolean;
    keyboardFocused?: boolean;
    value?: InputValue;
    readOnly?: boolean;
    label?: ReactNode;
}

export const DropdownItemContainer = styled(Stack)<DropdownItemContainerProps>`
    box-sizing: border-box;
    border-radius: ${getToken('borderRadius100')}px;
    cursor: ${({ disabled, readOnly }) => {
        if (readOnly) return 'default';
        if (disabled) return 'not-allowed';
        return 'pointer';
    }};
    outline: none;
    min-height: ${getToken('sizing500')}px;
    padding: ${getSpacingToken('100')}px ${getSpacingToken('150')}px;

    ${MEDIA_QUERY.largePhoneMax} {
        min-height: ${getToken('sizing700')}px;
        padding-top: ${getSpacingToken('150')}px;
        padding-bottom: ${getSpacingToken('150')}px;
    }

    ${COLOR_TRANSITION};

    ${({ disabled, readOnly }) =>
        disabled &&
        !readOnly &&
        css`
            & *,
            & path {
                color: ${getToken('textDisabled')};
                fill: ${getToken('textDisabled')};
            }
        `}

    ${({ disabled }) =>
        !disabled &&
        css`
            &:hover {
                background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
            }
            &:active {
                background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
            }
            &:focus-visible {
                ${FOCUS_OUTLINE_STYLE}
            }
        `}

    ${({ selected }) =>
        selected &&
        css`
            background-color: ${getToken('backgroundInteractiveSecondaryDefault')};

            &:hover {
                background-color: ${getToken('backgroundInteractiveSecondaryHover')};
            }
            &:active {
                background-color: ${getToken('backgroundInteractiveSecondaryActive')};
            }
        `}

    /* Keyboard navigation - blue outline */
    ${({ keyboardFocused }) =>
        keyboardFocused &&
        css`
            ${FOCUS_OUTLINE_STYLE}
        `}

    /* Mouse hover - grey background only (no outline) */
    ${({ focused, keyboardFocused, disabled }) =>
        focused &&
        !keyboardFocused &&
        !disabled &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
        `}

    /* Mouse hover + selected - grey background only */
    ${({ focused, keyboardFocused, selected, disabled }) =>
        focused &&
        !keyboardFocused &&
        selected &&
        !disabled &&
        css`
            background-color: ${getToken('backgroundInteractiveSecondaryHover')};
        `}
`;

export const DropdownItemSelectedContainer = styled.div`
    padding: ${getSpacingToken('0')}px ${getSpacingToken('50')}px ${getSpacingToken('0')}px ${getSpacingToken('100')}px;
    font-size: 0;
`;

export const LeftDetailContainer = styled.div`
    display: inline-flex;
`;

export const RightDetailContainer = styled.div`
    display: inline-flex;
`;

interface LabelProps extends Omit<TypographyProps, 'as'> {
    isMobile?: boolean;
    variant?: DropdownItemVariant;
}

export const Label = styled(Typography).attrs(({ isMobile, variant, as }: LabelProps & { as?: ElementType }) => ({
    textStyle: isMobile ? 'bodyLargeStrong' : 'bodyMediumStrong',
    variant,
    as: as || 'div',
}))<LabelProps>``;
